<script>
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import { fetchCategorias } from '../controllers/categoriaController.js';
    import { fetchProductos } from '../controllers/productoController.js';

    // State
    let categorias = [];
    let productos = [];
    let reporteData = [];
    let cargando = false;
    let tipoReporte = 'productos_por_categoria';
    let fechaInicio = '';
    let fechaFin = '';

    // Cargar datos al montar el componente
    onMount(async () => {
        await cargarDatos();
        await generarReporte();
    });

    async function cargarDatos() {
        try {
            cargando = true;
            const [categoriasResponse, productosResponse] = await Promise.all([
                fetchCategorias(1, 1000),
                fetchProductos(1, 1000)
            ]);
            
            if (categoriasResponse && categoriasResponse.categoria) {
                categorias = categoriasResponse.categoria;
            }
            
            if (productosResponse && productosResponse.producto) {
                productos = productosResponse.producto;
            }
        } catch (error) {
            console.error('Error cargando datos:', error);
        } finally {
            cargando = false;
        }
    }

    async function generarReporte() {
        try {
            cargando = true;
            
            switch (tipoReporte) {
                case 'productos_por_categoria':
                    reporteData = generarReporteProductosPorCategoria();
                    break;
                case 'categorias_mas_populares':
                    reporteData = generarReporteCategoriasPopulares();
                    break;
                case 'productos_sin_categoria':
                    reporteData = generarReporteProductosSinCategoria();
                    break;
                case 'estadisticas_categorias':
                    reporteData = generarEstadisticasCategorias();
                    break;
                default:
                    reporteData = [];
            }
        } catch (error) {
            console.error('Error generando reporte:', error);
        } finally {
            cargando = false;
        }
    }

    function generarReporteProductosPorCategoria() {
        const data = [];
        
        categorias.forEach(categoria => {
            const productosEnCategoria = productos.filter(producto => 
                producto.categorias && producto.categorias.some(cat => 
                    cat.categoria && cat.categoria.id === categoria.id
                )
            );
            
            if (productosEnCategoria.length > 0) {
                const precioTotal = productosEnCategoria.reduce((sum, p) => sum + (p.precio || 0), 0);
                const precioPromedio = precioTotal / productosEnCategoria.length;
                
                data.push({
                    categoria: categoria.descrip,
                    cantidadProductos: productosEnCategoria.length,
                    precioTotal: precioTotal,
                    precioPromedio: precioPromedio,
                    productosActivos: productosEnCategoria.filter(p => p.sta).length,
                    productosInactivos: productosEnCategoria.filter(p => !p.sta).length
                });
            }
        });
        
        return data.sort((a, b) => b.cantidadProductos - a.cantidadProductos);
    }

    function generarReporteCategoriasPopulares() {
        const categoriaCounts = {};
        
        productos.forEach(producto => {
            if (producto.categorias) {
                producto.categorias.forEach(cat => {
                    if (cat.categoria) {
                        const categoriaId = cat.categoria.id;
                        const categoriaDescrip = cat.categoria.descrip;
                        
                        if (!categoriaCounts[categoriaId]) {
                            categoriaCounts[categoriaId] = {
                                descrip: categoriaDescrip,
                                count: 0,
                                productos: []
                            };
                        }
                        
                        categoriaCounts[categoriaId].count++;
                        categoriaCounts[categoriaId].productos.push(producto);
                    }
                });
            }
        });
        
        return Object.values(categoriaCounts)
            .sort((a, b) => b.count - a.count)
            .slice(0, 10); // Top 10 categorías más populares
    }

    function generarReporteProductosSinCategoria() {
        return productos.filter(producto => 
            !producto.categorias || producto.categorias.length === 0
        );
    }

    function generarEstadisticasCategorias() {
        const totalProductos = productos.length;
        const productosConCategoria = productos.filter(p => 
            p.categorias && p.categorias.length > 0
        ).length;
        const productosSinCategoria = totalProductos - productosConCategoria;
        
        const totalCategorias = categorias.length;
        const categoriasConProductos = categorias.filter(cat => 
            productos.some(p => 
                p.categorias && p.categorias.some(c => c.categoria && c.categoria.id === cat.id)
            )
        ).length;
        
        return [{
            totalProductos,
            productosConCategoria,
            productosSinCategoria,
            totalCategorias,
            categoriasConProductos,
            categoriasSinProductos: totalCategorias - categoriasConProductos,
            porcentajeProductosConCategoria: totalProductos > 0 ? (productosConCategoria / totalProductos * 100).toFixed(2) : 0,
            porcentajeCategoriasConProductos: totalCategorias > 0 ? (categoriasConProductos / totalCategorias * 100).toFixed(2) : 0
        }];
    }

    function exportarReporte() {
        let csvContent = '';
        
        if (tipoReporte === 'estadisticas_categorias') {
            const stats = reporteData[0];
            csvContent = `Métrica,Valor\n`;
            csvContent += `Total Productos,${stats.totalProductos}\n`;
            csvContent += `Productos con Categoría,${stats.productosConCategoria}\n`;
            csvContent += `Productos sin Categoría,${stats.productosSinCategoria}\n`;
            csvContent += `Total Categorías,${stats.totalCategorias}\n`;
            csvContent += `Categorías con Productos,${stats.categoriasConProductos}\n`;
            csvContent += `Categorías sin Productos,${stats.categoriasSinProductos}\n`;
            csvContent += `% Productos con Categoría,${stats.porcentajeProductosConCategoria}%\n`;
            csvContent += `% Categorías con Productos,${stats.porcentajeCategoriasConProductos}%\n`;
        } else {
            const headers = Object.keys(reporteData[0] || {});
            csvContent = headers.join(',') + '\n';
            csvContent += reporteData.map(row => 
                headers.map(header => row[header] || '').join(',')
            ).join('\n');
        }
        
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `reporte_categorias_${tipoReporte}_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    }
</script>

<div class="container mx-auto px-4 py-8">
    <div class="bg-white rounded-lg shadow-lg p-6">
        <h1 class="text-3xl font-bold text-gray-800 mb-6">{$t('categories.categoryReports')}</h1>
        
        <!-- Controles del Reporte -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
                <label for="tipoReporte" class="block text-sm font-medium text-gray-700 mb-2">
                    {$t('categories.reportType')}
                </label>
                <select 
                    id="tipoReporte"
                    bind:value={tipoReporte}
                    on:change={generarReporte}
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                    <option value="productos_por_categoria">{$t('categories.productsByCategory')}</option>
                    <option value="categorias_mas_populares">{$t('categories.mostPopularCategories')}</option>
                    <option value="productos_sin_categoria">{$t('categories.productsWithoutCategory')}</option>
                    <option value="estadisticas_categorias">{$t('categories.categoryStatistics')}</option>
                </select>
            </div>
            
            <div class="flex items-end">
                <button 
                    on:click={generarReporte}
                    class="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200"
                >
                    {$t('categories.generateReport')}
                </button>
            </div>
            
            <div class="flex items-end">
                <button 
                    on:click={exportarReporte}
                    disabled={reporteData.length === 0}
                    class="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-medium py-2 px-4 rounded-md transition duration-200"
                >
                    {$t('categories.exportCsv')}
                </button>
            </div>
        </div>

        <!-- Resultados del Reporte -->
        {#if cargando}
            <div class="flex justify-center items-center py-8">
                <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span class="ml-2 text-gray-600">{$t('categories.generatingReport')}</span>
            </div>
        {:else if reporteData.length === 0}
            <div class="text-center py-8">
                <p class="text-gray-500 text-lg">{$t('categories.noDataToShow')}</p>
            </div>
        {:else}
            <div class="overflow-x-auto">
                {#if tipoReporte === 'estadisticas_categorias'}
                    <!-- Estadísticas Generales -->
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        {#each Object.entries(reporteData[0]) as [key, value]}
                            <div class="bg-blue-50 rounded-lg p-4 border border-blue-200">
                                <h3 class="text-sm font-medium text-blue-600 uppercase tracking-wide">{key.replace(/([A-Z])/g, ' $1').trim()}</h3>
                                <p class="text-2xl font-bold text-blue-800 mt-2">{value}</p>
                            </div>
                        {/each}
                    </div>
                {:else if tipoReporte === 'productos_por_categoria'}
                    <!-- Productos por Categoría -->
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Total</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Promedio</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activos</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Inactivos</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            {#each reporteData as row}
                                <tr>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.categoria}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.cantidadProductos}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${row.precioTotal.toFixed(2)}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${row.precioPromedio.toFixed(2)}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-green-600">{row.productosActivos}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-red-600">{row.productosInactivos}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else if tipoReporte === 'categorias_mas_populares'}
                    <!-- Categorías Más Populares -->
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-50">
                            <tr>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Categoría</th>
                                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cantidad de Productos</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                            {#each reporteData as row}
                                <tr>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.descrip}</td>
                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{row.count}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {:else if tipoReporte === 'productos_sin_categoria'}
                    <!-- Productos sin Categoría -->
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {#each reporteData as producto}
                            <div class="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                <h3 class="text-lg font-semibold text-gray-800 mb-2">{producto.nombre}</h3>
                                <p class="text-gray-600 text-sm mb-2">{producto.descrip}</p>
                                <p class="text-lg font-bold text-green-600">${producto.precio}</p>
                                <span class="inline-block bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded-full mt-2">
                                    Sin categoría
                                </span>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</div>
